/**
 * F-JP-21 and F-JP-22 — the deletion transaction and its crash recovery.
 *
 * A journal deletion is never a sequence of removals. Removing an entry, its
 * same-id draft and its attachment directory one call at a time has a state
 * after every call in which some of them are gone and the rest are not, and a
 * crash there leaves media the library still references or an entry whose media
 * has already been reclaimed. So every target is *moved* into a staging bundle
 * behind a durable manifest, and only once every move is verified is a
 * `committed` marker written. Before that marker the transaction is reversible;
 * after it the item is gone and removing the bundle is housekeeping.
 *
 * Three rules follow from that and each one is a real failure this prevents:
 *
 *  - **A post-commit garbage-collection failure is not a failed deletion.** The
 *    record is gone the moment the marker is durable. Reporting it as still
 *    present would invite a second delete of something that no longer exists.
 *  - **A rollback that cannot be verified throws `deletionNotVerified` and
 *    leaves the bundle.** Half-restored is the one state that must be visible,
 *    because the bundle is where the missing files are.
 *  - **Recovery skips what it cannot prove.** A bundle with an unreadable
 *    manifest, the wrong schema version, no targets, or a single unapproved
 *    path or unsafe name is left alone — never guessed at, never destroyed. A
 *    recovery pass that guesses is a deletion nobody asked for.
 */

import type { JournalRepositoryErrorCode } from "@ff/domain";
import { JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION } from "@ff/domain";

import { canonicalJson } from "../kernel/canonicalJson";
import type { JournalFileStore } from "./files";
import { journalBytesEqual, journalPathJoin, journalPathParent } from "./files";
import {
  JOURNAL_DELETE_COMMITTED_NAME,
  JOURNAL_DELETE_MANIFEST_NAME,
  JOURNAL_STAGING_DIRECTORY,
  isApprovedJournalDeletionPath,
  isJournalDeleteBundleName,
  isSafeJournalName,
  journalDeleteBundlePath,
} from "./layout";
import { journalUtf8Decode, journalUtf8Encode } from "./utf8";

/* ------------------------------------------------------------------------- *
 * Requests and outcomes
 * ------------------------------------------------------------------------- */

/**
 * One thing to remove.
 *
 * A directory target exists because an entry owns a whole attachment folder and
 * the two must commit together; it is expanded into its files at plan time so
 * the manifest names every byte the transaction is responsible for, rather than
 * a folder whose contents a later recovery pass would have to guess at.
 */
export interface JournalDeletionRequest {
  readonly kind: "file" | "directory";
  readonly path: string;
  /** One of the fixed staged names in `layout.ts`; must be a safe segment. */
  readonly stagedName: string;
}

export type JournalDeletionOutcome =
  | { readonly kind: "deleted" }
  /** None of the targets existed. A no-op, not an error. */
  | { readonly kind: "nothingToDelete" }
  | { readonly kind: "failed"; readonly error: JournalRepositoryErrorCode };

/* ------------------------------------------------------------------------- *
 * The manifest
 * ------------------------------------------------------------------------- */

interface ManifestTarget {
  readonly kind: "file" | "directory";
  readonly path: string;
  readonly stagedName: string;
  /** Child file names for a directory target; empty for a file target. */
  readonly files: readonly string[];
}

interface Manifest {
  readonly schemaVersion: number;
  readonly bundle: string;
  readonly targets: readonly ManifestTarget[];
}

/**
 * Reads a manifest without trusting any of it.
 *
 * Every rejection here is a bundle that will be skipped rather than acted on,
 * so the checks are deliberately exhaustive: this function's answer decides
 * whether files get moved.
 */
function readManifest(bytes: Uint8Array): Manifest | undefined {
  const text = journalUtf8Decode(bytes);
  if (text === undefined) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return undefined;
  }

  const source = parsed as Record<string, unknown>;
  if (source["schemaVersion"] !== JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION) return undefined;
  const bundle = source["bundle"];
  if (typeof bundle !== "string" || !isSafeJournalName(bundle)) return undefined;

  const rawTargets = source["targets"];
  if (!Array.isArray(rawTargets) || rawTargets.length === 0) return undefined;

  const targets: ManifestTarget[] = [];
  const staged = new Set<string>();
  for (const raw of rawTargets) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const target = raw as Record<string, unknown>;
    const kind = target["kind"];
    const path = target["path"];
    const stagedName = target["stagedName"];
    const files = target["files"];

    if (kind !== "file" && kind !== "directory") return undefined;
    if (typeof path !== "string" || !isApprovedJournalDeletionPath(path)) return undefined;
    if (typeof stagedName !== "string" || !isSafeJournalName(stagedName)) return undefined;
    if (staged.has(stagedName)) return undefined;
    staged.add(stagedName);
    if (!Array.isArray(files)) return undefined;
    const names: string[] = [];
    for (const name of files) {
      if (typeof name !== "string" || !isSafeJournalName(name)) return undefined;
      names.push(name);
    }
    if (kind === "file" && names.length > 0) return undefined;
    targets.push({ kind, path, stagedName, files: names });
  }

  return { schemaVersion: JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION, bundle, targets };
}

/** One staged file: where it came from and where it is parked. */
interface StagedFile {
  readonly origin: string;
  readonly staged: string;
}

function stagedFiles(bundle: string, manifest: Manifest): readonly StagedFile[] {
  const files: StagedFile[] = [];
  for (const target of manifest.targets) {
    if (target.kind === "file") {
      files.push({
        origin: target.path,
        staged: journalPathJoin(bundle, target.stagedName),
      });
      continue;
    }
    for (const name of target.files) {
      files.push({
        origin: journalPathJoin(target.path, name),
        staged: journalPathJoin(bundle, target.stagedName, name),
      });
    }
  }
  return files;
}

/* ------------------------------------------------------------------------- *
 * Running a transaction
 * ------------------------------------------------------------------------- */

/**
 * Moves every existing target into a bundle, commits, then garbage-collects.
 *
 * `bundleId` is supplied by the caller for the same reason the verified write's
 * token is: a store may not reach for a clock or a random number, and the one
 * piece of non-determinism in the bundle's name belongs to whoever owns the
 * platform.
 */
export async function runJournalDeletion(
  files: JournalFileStore,
  bundleId: string,
  requests: readonly JournalDeletionRequest[],
): Promise<JournalDeletionOutcome> {
  const rejection = rejectBadRequests(bundleId, requests);
  if (rejection !== undefined) return { kind: "failed", error: rejection };

  const bundle = journalDeleteBundlePath(bundleId);

  let targets: ManifestTarget[];
  try {
    targets = await plan(files, requests);
  } catch {
    return { kind: "failed", error: "ioFailure" };
  }
  if (targets.length === 0) return { kind: "nothingToDelete" };

  const manifest: Manifest = {
    schemaVersion: JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION,
    bundle: bundleId,
    targets,
  };
  const manifestPath = journalPathJoin(bundle, JOURNAL_DELETE_MANIFEST_NAME);
  const manifestBytes = journalUtf8Encode(canonicalJson(manifest));

  try {
    await files.createDirectory(bundle);
    await files.write(manifestPath, manifestBytes);
    const readBack = await files.read(manifestPath);
    if (readBack.kind !== "bytes" || !journalBytesEqual(readBack.bytes, manifestBytes)) {
      // Without a durable manifest a crash mid-move leaves files in a bundle
      // nothing knows how to put back. Nothing has moved yet, so refusing here
      // costs only the empty bundle.
      await removeBundle(files, bundle);
      return { kind: "failed", error: "deletionNotVerified" };
    }
  } catch {
    await removeBundle(files, bundle);
    return { kind: "failed", error: "ioFailure" };
  }

  const planned = stagedFiles(bundle, manifest);
  const moved: StagedFile[] = [];

  for (const file of planned) {
    const ok = await moveVerified(files, file.origin, file.staged);
    if (ok) {
      moved.push(file);
      continue;
    }
    // Pre-commit failure: undo in reverse order, so a target that was moved
    // last is the first one put back and no intermediate state has two files
    // claiming one path.
    return await rollbackMoves(files, bundle, moved, "ioFailure");
  }

  if (moved.length !== planned.length) {
    // Belt and braces against a future edit of the loop above: the manifest
    // says how many files this transaction owns, and committing with fewer
    // would strand the difference in the bundle forever.
    return await rollbackMoves(files, bundle, moved, "deletionNotVerified");
  }

  const committedPath = journalPathJoin(bundle, JOURNAL_DELETE_COMMITTED_NAME);
  const committedBytes = journalUtf8Encode(
    canonicalJson({ schemaVersion: JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION, bundle: bundleId }),
  );
  try {
    await files.write(committedPath, committedBytes);
    const readBack = await files.read(committedPath);
    if (readBack.kind !== "bytes" || !journalBytesEqual(readBack.bytes, committedBytes)) {
      return await rollbackMoves(files, bundle, moved, "deletionNotVerified");
    }
  } catch {
    return await rollbackMoves(files, bundle, moved, "ioFailure");
  }

  // Committed. Everything from here is housekeeping, and a failure in it must
  // not turn a completed deletion into a reported one.
  await collect(files, bundle, manifest);
  return { kind: "deleted" };
}

function rejectBadRequests(
  bundleId: string,
  requests: readonly JournalDeletionRequest[],
): JournalRepositoryErrorCode | undefined {
  // `unsafeRemovalPath` covers every "this request is not one we may act on"
  // refusal, matching `unsafeRecoveryPath` on the read side. The alternative
  // would be a generic `ioFailure`, which says storage broke when in fact
  // nothing was even attempted.
  if (!isSafeJournalName(bundleId)) return "unsafeRemovalPath";
  if (requests.length === 0) return "unsafeRemovalPath";

  const staged = new Set<string>();
  for (const request of requests) {
    if (!isApprovedJournalDeletionPath(request.path)) return "unsafeRemovalPath";
    if (!isSafeJournalName(request.stagedName)) return "unsafeRemovalPath";
    if (staged.has(request.stagedName)) return "unsafeRemovalPath";
    staged.add(request.stagedName);
  }
  return undefined;
}

/** Keeps only the targets that actually exist, and names a directory's files. */
async function plan(
  files: JournalFileStore,
  requests: readonly JournalDeletionRequest[],
): Promise<ManifestTarget[]> {
  const targets: ManifestTarget[] = [];
  for (const request of requests) {
    if (request.kind === "file") {
      if (!(await files.fileExists(request.path))) continue;
      targets.push({ kind: "file", path: request.path, stagedName: request.stagedName, files: [] });
      continue;
    }
    if (!(await files.directoryExists(request.path))) continue;
    const names = await files.list(request.path);
    const children: string[] = [];
    for (const name of names) {
      if (!isSafeJournalName(name)) continue;
      if (await files.fileExists(journalPathJoin(request.path, name))) children.push(name);
    }
    // An empty attachment directory has nothing to move; leaving it out keeps
    // the manifest's count equal to the number of files the transaction owns.
    if (children.length === 0) continue;
    targets.push({
      kind: "directory",
      path: request.path,
      stagedName: request.stagedName,
      files: children.sort(),
    });
  }
  return targets;
}

/**
 * Moves one file and proves it moved.
 *
 * Existence on both ends rather than a byte comparison: an attachment runs to a
 * gigabyte, and buffering it to prove a rename did not rewrite it would trade a
 * real memory failure for an imaginary correctness one.
 */
async function moveVerified(
  files: JournalFileStore,
  from: string,
  to: string,
): Promise<boolean> {
  try {
    await files.createDirectory(journalPathParent(to));
    await files.move(from, to);
    if (await files.fileExists(from)) return false;
    return await files.fileExists(to);
  } catch {
    return false;
  }
}

async function rollbackMoves(
  files: JournalFileStore,
  bundle: string,
  moved: readonly StagedFile[],
  error: JournalRepositoryErrorCode,
): Promise<JournalDeletionOutcome> {
  let restored = true;
  for (const file of [...moved].reverse()) {
    if (!(await moveVerified(files, file.staged, file.origin))) restored = false;
  }
  if (!restored) {
    // The bundle stays. It is where the files that did not make it back are,
    // and it is the only thing a later recovery pass can act on.
    return { kind: "failed", error: "deletionNotVerified" };
  }
  await removeBundle(files, bundle);
  return { kind: "failed", error };
}

/** Best-effort cleanup after commit. Never reported. */
async function collect(
  files: JournalFileStore,
  bundle: string,
  manifest: Manifest,
): Promise<void> {
  for (const target of manifest.targets) {
    if (target.kind !== "directory") continue;
    try {
      // The owner directory is empty now; leaving it would make a later
      // attachment sweep walk a tree of empty folders forever.
      await files.removeDirectory(target.path);
    } catch {
      // An orphaned empty directory costs nothing.
    }
  }
  await removeBundle(files, bundle);
}

async function removeBundle(files: JournalFileStore, bundle: string): Promise<void> {
  try {
    await files.removeDirectory(bundle);
  } catch {
    // A bundle that will not delete is collected by the next recovery pass.
  }
}

/* ------------------------------------------------------------------------- *
 * Crash recovery (F-JP-22)
 * ------------------------------------------------------------------------- */

export interface JournalDeletionRecoveryReport {
  /** Committed bundles whose staging was reclaimed. */
  readonly collected: number;
  /** Uncommitted bundles whose targets were put back. */
  readonly rolledBack: number;
  /** Bundles left exactly as they are, because acting would be guessing. */
  readonly skipped: number;
}

/**
 * Runs on every availability check.
 *
 * Committed bundles are garbage-collected; uncommitted bundles are rolled back;
 * anything that cannot be read with certainty is skipped. Nothing here ever
 * deletes a file it did not find named in a manifest it fully validated.
 */
export async function recoverJournalDeletions(
  files: JournalFileStore,
): Promise<JournalDeletionRecoveryReport> {
  const report = { collected: 0, rolledBack: 0, skipped: 0 };

  const names = await files.list(JOURNAL_STAGING_DIRECTORY);
  for (const name of names) {
    if (!isJournalDeleteBundleName(name)) continue;
    const bundle = journalPathJoin(JOURNAL_STAGING_DIRECTORY, name);
    if (!(await files.directoryExists(bundle))) continue;

    const read = await files.read(journalPathJoin(bundle, JOURNAL_DELETE_MANIFEST_NAME));
    if (read.kind !== "bytes") {
      report.skipped += 1;
      continue;
    }
    const manifest = readManifest(read.bytes);
    if (manifest === undefined) {
      report.skipped += 1;
      continue;
    }

    if (await files.fileExists(journalPathJoin(bundle, JOURNAL_DELETE_COMMITTED_NAME))) {
      await collect(files, bundle, manifest);
      report.collected += 1;
      continue;
    }

    if (await rollbackBundle(files, bundle, manifest)) {
      await removeBundle(files, bundle);
      report.rolledBack += 1;
    } else {
      report.skipped += 1;
    }
  }

  return report;
}

/**
 * Puts an uncommitted bundle's files back.
 *
 * A staged file whose origin is occupied again is left in the bundle rather
 * than moved over the occupant: the transaction never committed, so whatever is
 * at that path now is newer than what the bundle holds, and overwriting it
 * would undo a save the user made after the crash.
 */
async function rollbackBundle(
  files: JournalFileStore,
  bundle: string,
  manifest: Manifest,
): Promise<boolean> {
  let complete = true;
  for (const file of [...stagedFiles(bundle, manifest)].reverse()) {
    try {
      if (!(await files.fileExists(file.staged))) continue;
      if (await files.fileExists(file.origin)) {
        complete = false;
        continue;
      }
    } catch {
      complete = false;
      continue;
    }
    if (!(await moveVerified(files, file.staged, file.origin))) complete = false;
  }
  return complete;
}
